import { createSlice } from '@reduxjs/toolkit';
import { loadCustomizerSettings, saveCustomizerSettings } from '@/utils/localStorage';

interface StateType {
  activeDir?: string | any;
  activeMode?: string; // This can be light or dark
  activeTheme?: string; // BLUE_THEME, GREEN_THEME, BLACK_THEME, PURPLE_THEME, ORANGE_THEME
  SidebarWidth?: number;
  MiniSidebarWidth?: number;
  TopbarHeight?: number;
  isCollapse?: boolean;
  isLayout?: string;
  isSidebarHover?: boolean;
  isMobileSidebar?: boolean;
  isHorizontal?: boolean;
  isLanguage?: string;
  isCardShadow?: boolean;
  borderRadius?: number | any;
}

// Valores padrão
const defaultState: StateType = {
  activeDir: 'ltr',
  activeMode: 'light', // This can be light or dark
  activeTheme: 'ORANGE_THEME', // BLUE_THEME, GREEN_THEME, BLACK_THEME, PURPLE_THEME, ORANGE_THEME
  SidebarWidth: 265,
  MiniSidebarWidth: 87,
  TopbarHeight: 70,
  isLayout: 'full', // This can be full or boxed
  isCollapse: false, // to make sidebar Mini by default
  isSidebarHover: false,
  isMobileSidebar: false,
  isHorizontal: false,
  isLanguage: 'en',
  isCardShadow: true,
  borderRadius: 6,
};

// Carregar configurações salvas ou usar padrões
const getInitialState = (): StateType => {
  const savedSettings = loadCustomizerSettings();
  return {
    ...defaultState,
    ...savedSettings,
  };
};

const initialState: StateType = getInitialState();

export const CustomizerSlice = createSlice({
  name: 'customizer',
  initialState,
  reducers: {
    setTheme: (state: StateType, action) => {
      state.activeTheme = action.payload;
      saveCustomizerSettings({ activeTheme: action.payload });
    },
    setDarkMode: (state: StateType, action) => {
      state.activeMode = action.payload;
      saveCustomizerSettings({ activeMode: action.payload });
    },

    setDir: (state: StateType, action) => {
      state.activeDir = action.payload;
      saveCustomizerSettings({ activeDir: action.payload });
    },
    setLanguage: (state: StateType, action) => {
      state.isLanguage = action.payload;
      saveCustomizerSettings({ isLanguage: action.payload });
    },
    setCardShadow: (state: StateType, action) => {
      state.isCardShadow = action.payload;
      saveCustomizerSettings({ isCardShadow: action.payload });
    },
    toggleSidebar: (state) => {
      state.isCollapse = !state.isCollapse;
      saveCustomizerSettings({ isCollapse: state.isCollapse });
    },
    hoverSidebar: (state: StateType, action) => {
      state.isSidebarHover = action.payload;
      // Não persistir hover state pois é temporário
    },
    toggleMobileSidebar: (state) => {
      state.isMobileSidebar = !state.isMobileSidebar;
      // Não persistir mobile sidebar pois é responsivo
    },
    toggleLayout: (state: StateType, action) => {
      state.isLayout = action.payload;
      saveCustomizerSettings({ isLayout: action.payload });
    },
    toggleHorizontal: (state: StateType, action) => {
      state.isHorizontal = action.payload;
      // Não persistir por enquanto, mas pode ser adicionado se necessário
    },
    setBorderRadius: (state: StateType, action) => {
      state.borderRadius = action.payload;
      saveCustomizerSettings({ borderRadius: action.payload });
    },
    // Nova action para resetar configurações
    resetCustomizer: (state) => {
      Object.assign(state, defaultState);
      saveCustomizerSettings(defaultState);
    },
    // Nova action para carregar configurações salvas
    loadSavedSettings: (state) => {
      const savedSettings = loadCustomizerSettings();
      Object.assign(state, { ...defaultState, ...savedSettings });
    },
  },
});

export const {
  setTheme,
  setDarkMode,
  setDir,
  toggleSidebar,
  hoverSidebar,
  toggleMobileSidebar,
  toggleLayout,
  setBorderRadius,
  toggleHorizontal,
  setLanguage,
  setCardShadow,
  resetCustomizer,
  loadSavedSettings,
} = CustomizerSlice.actions;

export default CustomizerSlice.reducer;
